import React, { useEffect, useRef } from 'react';


import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import { Checkbox } from 'ui/checkbox';
import { Radio } from 'ui/radio';
import { RadioGroup } from 'ui/radio-group';

import PopulationCriteriaBlock from './population-criteria-block';

import Icons from 'assets/icons';

import {
  BASELINE_POPULATIONS,
  POPULATION_TYPES,
} from '../matrix-step.constants';

import type { PopulationType } from '../matrix-step.constants';
import type {
  PopulationCriteriaBlock as CriteriaBlock,
  PopulationCriteriaBlockErrors,
  SelectOption,
} from '../hooks/use-populations-draft';

type PopulationsTabContentProps = {
  populationType: PopulationType;
  onPopulationTypeChange: (type: PopulationType) => void;
  baselineSelected: string | null;
  toggleBaseline: (value: string) => void;
  criteriaBlocks: CriteriaBlock[];
  criteriaErrors: Record<string, PopulationCriteriaBlockErrors>;
  canAddCriteriaBlock: boolean;
  getDimensionOptionsForBlock: (blockId: string) => SelectOption[];
  getValueOptions: (dimension: string | null) => SelectOption[];
  onAddCriteriaBlock: () => void;
  onRemoveCriteriaBlock: (blockId: string) => void;
  onCriteriaDimensionChange: (
    blockId: string,
    dimension: string | null
  ) => void;
  onCriteriaValuesChange: (blockId: string, values: string[]) => void;
  readOnly?: boolean;
  pellSelected: boolean;
};

const PopulationsTabContent = ({
  populationType,
  onPopulationTypeChange,
  baselineSelected,
  toggleBaseline,
  criteriaBlocks,
  criteriaErrors,
  canAddCriteriaBlock,
  getDimensionOptionsForBlock,
  getValueOptions,
  onAddCriteriaBlock,
  onRemoveCriteriaBlock,
  onCriteriaDimensionChange,
  onCriteriaValuesChange,
  readOnly,
  pellSelected,
}: PopulationsTabContentProps) => {
  const isBaseline = populationType === POPULATION_TYPES.BASELINE;

  const criteriaListRef = useRef<HTMLDivElement>(null);
  const previousBlocksCountRef = useRef(criteriaBlocks.length);

  useEffect(() => {
    if (criteriaBlocks.length > previousBlocksCountRef.current) {
      criteriaListRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'end',
      });
    }

    previousBlocksCountRef.current = criteriaBlocks.length;
  }, [criteriaBlocks.length]);

  return (
    <Box
      sx={{ position: 'relative', p: 1.5 }}
      data-testid="populations-tab-content"
      inert={readOnly}
    >
      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="sr-only">Population type</legend>
        <Stack sx={{ gap: 1 }}>
          <Radio
            value={POPULATION_TYPES.BASELINE}
            label="Based on baseline populations"
            id="pop-type-baseline"
            name="population-type"
            checked={populationType === POPULATION_TYPES.BASELINE}
            onChange={() => onPopulationTypeChange(POPULATION_TYPES.BASELINE)}
          />
          <RadioGroup
            aria-label="Baseline population"
            row
            name="baseline-population"
            value={baselineSelected ?? ''}
            onChange={(_event, value) => toggleBaseline(value)}
            disabled={!isBaseline}
            sx={{ pl: 3.5 }}
          >
            {BASELINE_POPULATIONS.map((population) => (
              <Radio
                key={population.value}
                value={population.value}
                label={population.label}
                id={`baseline-${population.value}`}
              />
            ))}
          </RadioGroup>
        </Stack>

        <Radio
          value={POPULATION_TYPES.CUSTOM}
          label="Custom populations only"
          id="pop-type-custom"
          name="population-type"
          checked={populationType === POPULATION_TYPES.CUSTOM}
          onChange={() => onPopulationTypeChange(POPULATION_TYPES.CUSTOM)}
        />
      </fieldset>

      <Checkbox
        id="pell-recipients"
        label="Pell Recipients"
        value="pell"
        checked={pellSelected}
        disabled
        sx={{ mt: 2 }}
        data-testid="checkbox-pell-recipients"
      />

      <div
        ref={criteriaListRef}
        className="mt-4 flex max-w-[434px] scroll-mb-3 flex-col gap-4"
      >
        {criteriaBlocks.map((block) => (
          <PopulationCriteriaBlock
            key={block.blockId}
            block={block}
            errors={criteriaErrors[block.blockId]}
            dimensionOptions={getDimensionOptionsForBlock(block.blockId)}
            valueOptions={getValueOptions(block.dimension)}
            onDimensionChange={onCriteriaDimensionChange}
            onValuesChange={onCriteriaValuesChange}
            onRemove={onRemoveCriteriaBlock}
          />
        ))}

        <Button
          type="button"
          variant="reserve-outlined"
          size="sm"
          className="w-fit"
          iconBefore={<Icons.Plus />}
          disabled={!canAddCriteriaBlock}
          onClick={onAddCriteriaBlock}
          data-testid="add-population-criteria-button"
        >
          More criteria
        </Button>
      </div>
    </Box>
  );
};

export default PopulationsTabContent;
